import { useCallback, useEffect, useId, useRef, useState, type ComponentProps } from 'react'
import ContextPanel from './ContextPanel'
import { useDisposicion } from '../../useDisposicion'
import { escapeCierraElCajon } from '../../disposicion'
import { cuantosPorDecidir } from '../../documentosDelCaso'

/**
 * El panel del caso —Copiloto IA, Documentos, Ficha— en columna o en cajón
 * (DEF-031, QA de TEST 05-oct-2026).
 *
 * Cuando a la ventana le alcanza —con el sidebar que está en pantalla— para la
 * lista, el panel y el mínimo del chat, es la columna de siempre. Si no, el
 * panel pasa a un cajón que se abre con el botón del riel derecho y se monta
 * ENCIMA del chat: el portal entra en 768 px sin scroll lateral y el chat
 * conserva el ancho que su cabecera necesita. Lo decide `disposicion.ts`; lo
 * usan la bandeja y En vivo, para que las dos pantallas no se desalineen.
 *
 * Cuatro cosas que no se ven:
 * - **Hay UN solo `<ContextPanel>`, siempre en el mismo lugar del árbol**
 *   (DEF-052). Antes la columna era `return <ContextPanel/>` y el cajón otro
 *   árbol: al cruzar el umbral React lo desmontaba y montaba de nuevo, y se
 *   llevaba el visor de documentos abierto, la subida a medias y lo que el
 *   asesor tenía desplegado. Ahora cambia solo el envoltorio —`contents` en
 *   columna, el cajón absoluto si no—, y desde que el sidebar entra en la
 *   cuenta el umbral se cruza también al plegar el menú.
 * - **El cajón se esconde, no se desmonta.** `invisible` lo saca también del
 *   orden del teclado y del lector de pantalla.
 * - **Sin `transform`.** Un ancestro transformado se vuelve el bloque de
 *   referencia de lo `position: fixed`. El visor (`VisorDocumento`) además se
 *   monta en `document.body`, así que tampoco lo esconde el cajón cerrado.
 * - **El riel no pierde las señales del panel.** Cerrado, el asesor igual ve
 *   cuántos documentos hay por revisar y si hay una alerta crítica.
 *
 * El contenedor que lo envuelve tiene que ser `relative`: el cajón y el fondo
 * se posicionan contra él, o sea contra la zona del chat y no la lista.
 */
type Props = ComponentProps<typeof ContextPanel> & {
  /** El mínimo del chat de la pantalla: `CHAT_MINIMO_BANDEJA` o `_EN_VIVO`. */
  chatMinimo?: number
}

export default function PanelDelCaso({ chatMinimo, ...panel }: Props) {
  const disposicion = useDisposicion(chatMinimo)
  const enCajon = disposicion === 'cajon'
  const [abierto, setAbierto] = useState(false)
  const boton = useRef<HTMLButtonElement>(null)
  const cajon = useRef<HTMLDivElement>(null)
  const id = useId()

  const cerrar = useCallback(() => {
    setAbierto(false)
    // El foco vuelve a donde estaba: quien navega con teclado no tiene que
    // buscar de nuevo dónde quedó. Sin desplazar nada (DEF-031, 07-oct-2026):
    // `focus()` lleva el elemento a la vista moviendo TODO contenedor que se
    // pueda mover, y con la página más ancha que la ventana corría el
    // documento a la derecha y el menú quedaba afuera.
    boton.current?.focus({ preventScroll: true })
  }, [])

  useEffect(() => {
    if (!abierto) return
    // Al abrir, el foco entra al cajón; Tab recorre sus pestañas. Sin
    // desplazar la página (DEF-031), por lo mismo que al cerrar.
    cajon.current?.focus({ preventScroll: true })
    const alTeclear = (e: KeyboardEvent) => {
      const hayModal = Boolean(document.querySelector('[aria-modal="true"]'))
      if (escapeCierraElCajon(e.key, true, hayModal)) cerrar()
    }
    document.addEventListener('keydown', alTeclear)
    return () => document.removeEventListener('keydown', alTeclear)
  }, [abierto, cerrar])

  // Si la ventana se agranda hasta que entra la columna, el cajón se olvida:
  // al volver a achicarla, empieza cerrado.
  useEffect(() => { if (disposicion === 'columna') setAbierto(false) }, [disposicion])

  // La misma cuenta que el badge de «Documentos»: los requisitos que la IA
  // observó y esperan la decisión del ejecutivo (20-oct-2026).
  const porRevisar = cuantosPorDecidir(panel.detail.documents, panel.detail.progreso?.requeridos)
  const critica = Boolean(panel.detail.alerta_critica)

  return (
    <>
      {enCajon && (
        <div className="flex w-11 shrink-0 flex-col items-center border-l border-surface-border bg-white py-2.5">
          <button
            ref={boton}
            type="button"
            onClick={() => (abierto ? cerrar() : setAbierto(true))}
            aria-expanded={abierto}
            aria-controls={id}
            aria-label={abierto
              ? 'Cerrar el panel del caso'
              : 'Abrir el panel del caso: Copiloto IA, Documentos y Ficha'}
            title={abierto ? 'Cerrar el panel (Esc)' : 'Copiloto IA, Documentos y Ficha'}
            className={`flex flex-col items-center gap-2 rounded-btn border px-1.5 py-2.5 transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand ${
              abierto
                ? 'border-brand-border bg-brand-light text-brand-hover'
                : 'border-transparent text-ink-muted hover:border-surface-border hover:bg-surface hover:text-ink'
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <rect x="1.5" y="2.5" width="13" height="11" rx="2" stroke="currentColor" strokeWidth="1.3" />
              <path d="M10 2.5v11" stroke="currentColor" strokeWidth="1.3" />
            </svg>
            <span className="text-micro font-bold [writing-mode:vertical-rl]">
              Copiloto · Documentos · Ficha
            </span>
            {porRevisar > 0 && (
              <span className="rounded-chip bg-warn-light px-1.5 py-px text-[10px] font-bold text-warn"
                    title={`${porRevisar} requisito(s) observado(s) por decidir`}>
                {porRevisar}
              </span>
            )}
            {critica && (
              <span className="h-[7px] w-[7px] rounded-full bg-danger"
                    title="El expediente tiene una alerta crítica" />
            )}
          </button>
        </div>
      )}

      {/* El fondo: un clic afuera cierra, como en cualquier cajón. */}
      {enCajon && abierto && (
        <div className="absolute inset-0 z-20 bg-ink/20" onClick={cerrar} aria-hidden />
      )}

      {/* El MISMO envoltorio en las dos disposiciones: cambian sus atributos,
          no el elemento, así que el panel no se vuelve a montar (DEF-052). En
          columna es `contents` —no dibuja caja: el panel queda como columna
          de la fila, igual que antes—; en el cajón, el diálogo absoluto. */}
      <div
        id={id}
        ref={cajon}
        role={enCajon ? 'dialog' : undefined}
        aria-modal={enCajon ? 'false' : undefined}
        aria-label={enCajon ? 'Panel del caso' : undefined}
        tabIndex={enCajon ? -1 : undefined}
        className={enCajon
          ? `absolute inset-y-0 right-0 z-30 flex shadow-panel focus:outline-none ${
            abierto ? 'visible' : 'invisible'}`
          : 'contents'}
      >
        <ContextPanel {...panel} onCerrar={enCajon ? cerrar : undefined} />
      </div>
    </>
  )
}
